import React, { useState } from 'react';
import axios from 'axios';
import './POLookup.css';
import API_BASE_URL from '../config';

function POLookup({ onDataLoaded }) {
  const [poNumber, setPONumber] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [apiInfo, setApiInfo] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [debugData, setDebugData] = useState(null);

  // Validate PO format: PO + 8 digits
  const validatePOFormat = (value) => {
    const pattern = /^PO\d{8}$/i;
    return pattern.test(value.trim());
  };

  const handleInputChange = (e) => {
    const value = e.target.value.toUpperCase();
    setPONumber(value);
    setError('');
    setSuccess('');
    setApiInfo(null);
    setDebugData(null);
  };

  const handleLookup = async () => {
    const trimmedPO = poNumber.trim();

    if (!trimmedPO) {
      setError('กรุณาใส่เลข PO');
      return;
    }

    if (!validatePOFormat(trimmedPO)) {
      setError('รูปแบบ PO ไม่ถูกต้อง (ต้องเป็น PO ตามด้วยตัวเลข 8 หลัก เช่น PO68005946)');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');
    setApiInfo(null);
    setDebugData(null);

    try {
      const response = await axios.post(`${API_BASE_URL}/api/po/lookup`, {
        po_number: trimmedPO,
        showDebug: showDebug
      });

      if (response.data.success) {
        setSuccess(`พบข้อมูล PO: ${trimmedPO} (${response.data.totalRows} รายการ)`);

        if (response.data.environment) {
          setApiInfo({
            environment: response.data.environment,
            url: response.data.connectedUrl
          });
        }

        if (response.data.debug) {
          setDebugData(response.data.debug);
        }

        // Pass entire response with processedRows format (same as Google Sheet import)
        onDataLoaded(response.data);
      } else {
        setError(response.data.error || 'ไม่พบข้อมูล PO');
        if (response.data.debug) {
          setDebugData(response.data.debug);
          // Also show API info on error if available
          if (response.data.environment) {
            setApiInfo({
              environment: response.data.environment,
              url: response.data.connectedUrl
            });
          }
        }
      }
    } catch (err) {
      setError(err.response?.data?.error || 'เกิดข้อผิดพลาดในการเชื่อมต่อ');
      if (err.response?.data?.debug) {
        setDebugData(err.response.data.debug);
        if (err.response.data.environment) {
          setApiInfo({
            environment: err.response.data.environment,
            url: err.response.data.connectedUrl
          });
        }
      }
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleLookup();
    }
  };

  const isValidFormat = poNumber.trim() === '' || validatePOFormat(poNumber);

  return (
    <div className="po-lookup-card">
      <h2>Step 1: ค้นหาข้อมูล PO</h2>
      <p className="subtitle">ใส่เลข PO เพื่อดึงข้อมูลจากระบบ ClickBroker</p>

      <div className="input-group">
        <input
          type="text"
          placeholder="ใส่เลข PO (เช่น PO68005946)"
          value={poNumber}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          disabled={loading}
          className={!isValidFormat ? 'invalid' : ''}
          maxLength={10}
        />
        <button
          onClick={handleLookup}
          disabled={loading || !poNumber.trim()}
          className="btn-primary"
        >
          {loading ? '🔍 กำลังค้นหา...' : '🔍 ค้นหา PO'}
        </button>
      </div>

      <div style={{ marginTop: '10px', display: 'flex', alignItems: 'center', fontSize: '0.9em', color: '#666' }}>
        <input
          type="checkbox"
          id="chkDebug"
          checked={showDebug}
          onChange={(e) => setShowDebug(e.target.checked)}
          style={{ marginRight: '8px' }}
        />
        <label htmlFor="chkDebug">Show Debug Info (แสดงข้อมูลการเชื่อมต่อ)</label>
      </div>

      {!isValidFormat && (
        <div className="format-hint">
          รูปแบบ: PO + ตัวเลข 8 หลัก (เช่น PO68005946)
        </div>
      )}

      {error && (
        <div className="error-message">
          ❌ {error}
          {apiInfo && showDebug && (
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(0,0,0,0.1)', fontSize: '0.9em' }}>
              🌐 <strong>Target:</strong> {apiInfo.environment}
              <div style={{ opacity: 0.8, wordBreak: 'break-all' }}>{apiInfo.url}</div>
            </div>
          )}
          {debugData && showDebug && (
            <div style={{ marginTop: '8px', padding: '10px', background: 'rgba(255,255,255,0.5)', borderRadius: '4px', fontSize: '0.85em', textAlign: 'left' }}>
              <div><strong>Request Time:</strong> {debugData.requestTime}</div>
              <div><strong>Method:</strong> {debugData.requestMethod}</div>
              <div><strong>Status:</strong> {debugData.apiStatus || 'Error'}</div>
            </div>
          )}
        </div>
      )}

      {success && (
        <div className="success-message">
          ✅ {success}
          {apiInfo && (
            <div style={{ marginTop: '8px', paddingTop: '8px', borderTop: '1px solid rgba(0,0,0,0.1)' }}>
              🌐 <strong>Connected API:</strong> {apiInfo.environment}
            </div>
          )}
          {debugData && showDebug && (
            <div style={{ marginTop: '8px', padding: '10px', background: 'rgba(0,0,0,0.05)', borderRadius: '4px', fontSize: '0.85em', textAlign: 'left', color: '#333' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>🔍 Debug Information:</div>
              <div><strong>Request URL:</strong> {apiInfo?.url}</div>
              <div><strong>Request Time:</strong> {debugData.requestTime}</div>
              <div><strong>Method:</strong> {debugData.requestMethod}</div>
            </div>
          )}
        </div>
      )}

      <div className="info-box">
        <p><strong>หมายเหตุ:</strong> ระบบจะดึงข้อมูลจาก ClickBroker API และ map ข้อมูลให้อัตโนมัติ</p>
      </div>
    </div>
  );
}

export default POLookup;
